import { Autocomplete, Radio } from "@mui/material";
import classNames from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { IBrand } from "store/brandStore";

import StyledTextField from "../StyledTextField/StyledTextField";
import styles from "./style.module.scss";

interface BrandAutocomplete {
    /**
     * Array with all brands.
     */
    brands: IBrand[];
    /**
     * Triggers filtering of brands by useAPIPagination.
     */
    setChosenBrand: React.Dispatch<React.SetStateAction<string>>;
    /**
     * Name of the head company.
     */
    companyName: string | undefined;
    /**
     * CSS classname
     */
    className?: string;
    /**
     * True if label displayed above (flex column and not row)
     */
    asColumn?: boolean;
}
/**
 * Autocomplete specific to brands (for Management General/Stock and Advance Payment pages)
 */
const BrandAutocomplete = ({
    brands,
    setChosenBrand,
    companyName,
    className = "",
    asColumn = false,
}: BrandAutocomplete) => {
    const [t] = useTranslation();
    const [localeChosenBrand, setLocaleChosenBrand] = useState<IBrand[]>([]);
    /**
     * Function that personalises the rendering of the options in the autocomplete popover.
     * @param props Props from the MUI component.
     * @param option Object with the option and its values.
     * @param param2 Option to know if the option was selected.
     */
    const renderOptionAutocomplete = (props: any, option: any, { selected }: any) => {
        return (
            <li
                {...props}
                className={classNames({
                    [styles["selected"]]: selected,
                })}
            >
                <Radio size="small" sx={{ opacity: 0.8 }} checked={selected} />
                <span>{companyName && option.name === "Par défaut" ? companyName : option.name}</span>
            </li>
        );
    };

    /**
     * Updates setChosenBrand so that the useAPIPagination is triggered.
     * @param brand Brand selected.
     */
    const handleBrandChange = (brands: IBrand[] | null) => {
        const brand = (brands ?? []).at(-1);
        setLocaleChosenBrand(brand ? [brand] : []);
        if (brand) {
            brand.id != -1 ? setChosenBrand(brand?.name) : setChosenBrand("Par défaut");
        } else {
            setChosenBrand("");
        }
    };

    const renderInputBrands = (params: any) => (
        <StyledTextField {...params} placeholder={localeChosenBrand.length ? "" : t("Filters.AllFeminine")} />
    );

    const renderBrandChip = (value) => (
        <span className={styles["management-autocomplete-tags"]}>
            {value.map((option: IBrand) => (
                <span key={option.id} className={styles["management-autocomplete-tag"]}>
                    {companyName && option.name === "Par défaut" ? companyName : option.name}
                </span>
            ))}
        </span>
    );

    return (
        <div className={classNames({ [styles["brands-filter"]]: true, [styles["column"]]: asColumn })}>
            <span className="whitespace-nowrap">{t("Filters.Brands")}</span>
            <Autocomplete
                value={localeChosenBrand}
                className={className}
                size="small"
                multiple
                disableCloseOnSelect
                options={brands ?? {}}
                getOptionLabel={(option) => (companyName && option.name === "Par défaut" ? companyName : option.name)}
                renderInput={(params) => renderInputBrands(params)}
                renderOption={renderOptionAutocomplete}
                renderTags={renderBrandChip}
                componentsProps={{
                    popper: { className: `brand-autocomplete-popper` },
                }}
                onChange={(e: any, value) => handleBrandChange(value)}
                ListboxProps={{ className: "brand-autocomplete-list" }}
                noOptionsText={t("Filters.EmptyStateBrands")}
                openOnFocus={true}
            />
        </div>
    );
};

export default BrandAutocomplete;
